package com.evoapps.evoshift.domain.rosterimport

/** Someone of the team a name on the sheet can be matched to. */
data class ImportTeamMember(val uid: String, val displayName: String)

/** How a name on the sheet matched the team. */
sealed interface NameMatch {
    /** The same name (case, diacritics, spacing and word order aside). */
    data class Exact(val uid: String) : NameMatch

    /** Most likely this person: initials ("J. Kowalski"), a part of the name, a typo. Shown as a suggestion. */
    data class Likely(val uid: String) : NameMatch

    /** Several people fit equally well: the person chooses. */
    data class Ambiguous(val uids: List<String>) : NameMatch

    data object None : NameMatch
}

/**
 * SH-217: matches the names on a roster sheet to the team, from the surest to the loosest rule; the
 * first rule that finds anyone decides (one person: a match; several: ambiguous):
 * 1. the same words in any order ("Kowalski Jan" is "Jan Kowalski"), ignoring case, diacritics
 *    ("Łucja Żak" = "lucja zak"), dots and extra spaces;
 * 2. initials for some words ("J. Kowalski", "Kowalski J", "Jan K.");
 * 3. a part of the name ("Kowalski", or "Anna Nowak" for "Anna Maria Nowak");
 * 4. one or two letters wrong ("Jan Kowalsky").
 */
object NameMatcher {

    fun match(name: String, team: List<ImportTeamMember>): NameMatch {
        val words = words(name)
        if (words.isEmpty()) return NameMatch.None
        val people = team.map { it to words(it.displayName) }.filter { it.second.isNotEmpty() }

        fun decide(found: List<ImportTeamMember>, exact: Boolean): NameMatch? = when (found.distinctBy { it.uid }.size) {
            0 -> null
            1 -> if (exact) NameMatch.Exact(found.first().uid) else NameMatch.Likely(found.first().uid)
            else -> NameMatch.Ambiguous(found.map { it.uid }.distinct())
        }

        decide(people.filter { (_, theirs) -> theirs.sorted() == words.sorted() }.map { it.first }, exact = true)?.let { return it }
        if (words.any { it.length == 1 }) {
            decide(people.filter { (_, theirs) -> withInitials(words, theirs) }.map { it.first }, exact = false)?.let { return it }
        }
        decide(people.filter { (_, theirs) -> theirs.size > words.size && words.all { it in theirs } && words.any { it.length > 1 } }.map { it.first }, exact = false)
            ?.let { return it }
        val joined = words.sorted().joinToString(" ")
        if (joined.length >= 5) {
            val allowed = if (joined.length >= 10) 2 else 1
            decide(people.filter { (_, theirs) -> distance(joined, theirs.sorted().joinToString(" "), allowed) <= allowed }.map { it.first }, exact = false)
                ?.let { return it }
        }
        return NameMatch.None
    }

    /** Each of [sheet]'s words is a whole word of [theirs] or its initial, one word each, at least one whole. */
    private fun withInitials(sheet: List<String>, theirs: List<String>): Boolean {
        if (sheet.size != theirs.size || sheet.none { it.length > 1 }) return false
        val left = theirs.toMutableList()
        // Whole words first, so "J. Jankowski" does not spend "jankowski" on the initial.
        for (word in sheet.sortedByDescending { it.length }) {
            val at = if (word.length > 1) left.indexOf(word) else left.indexOfFirst { it.startsWith(word) }
            if (at < 0) return false
            left.removeAt(at)
        }
        return true
    }

    /** The words of a name, compared: lower case, without diacritics, dots, commas or hyphens. */
    fun words(name: String): List<String> = normalize(name).split(' ').filter { it.isNotEmpty() }

    /** [text] in lower case without diacritics; anything but letters and digits becomes a space. */
    fun normalize(text: String): String {
        val out = StringBuilder(text.length)
        for (c in text.lowercase()) {
            val plain = FOLDED[c]
            when {
                plain != null -> out.append(plain)
                c.isLetterOrDigit() -> out.append(c)
                else -> out.append(' ')
            }
        }
        return out.toString().split(' ').filter { it.isNotEmpty() }.joinToString(" ")
    }

    /** Edit distance of [a] and [b], or more than [limit] as soon as it is clear. */
    private fun distance(a: String, b: String, limit: Int): Int {
        if (kotlin.math.abs(a.length - b.length) > limit) return limit + 1
        var previous = IntArray(b.length + 1) { it }
        for (i in 1..a.length) {
            val current = IntArray(b.length + 1)
            current[0] = i
            var best = current[0]
            for (j in 1..b.length) {
                val cost = if (a[i - 1] == b[j - 1]) 0 else 1
                current[j] = minOf(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost)
                best = minOf(best, current[j])
            }
            if (best > limit) return limit + 1
            previous = current
        }
        return previous[b.length]
    }

    private val FOLDED: Map<Char, String> = buildMap {
        fun fold(chars: String, to: String) = chars.forEach { put(it, to) }
        fold("ąáàâäãåāă", "a"); fold("ćçčĉ", "c"); fold("ďđ", "d"); fold("ęéèêëěēė", "e")
        fold("íìîïīį", "i"); fold("łľĺ", "l"); fold("ńñňņ", "n"); fold("óòôöõőøō", "o")
        fold("řŕ", "r"); fold("śšşș", "s"); fold("ťţț", "t"); fold("úùûüůűūų", "u")
        fold("ýÿ", "y"); fold("źżž", "z"); put('ß', "ss")
        // Ukrainian and Russian spellings of the same letter.
        put('ё', "е"); put('ї', "і"); put('й', "и"); put('ґ', "г")
    }
}
